import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Icon from '../../../components/AppIcon';

const ProgressChart = ({ contributions, goal }) => {
  const chartData = contributions
    ?.slice()
    ?.reverse()
    ?.reduce((acc, contribution, index) => {
      const previousAmount = index > 0 ? acc?.[index - 1]?.cumulativeAmount : 0;
      const cumulativeAmount = previousAmount + contribution?.amount;
      
      acc?.push({
        date: new Date(contribution?.contributionDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: new Date(contribution?.contributionDate)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
        amount: contribution?.amount,
        cumulativeAmount: cumulativeAmount,
        isAutomatic: contribution?.isAutomatic
      });
      
      return acc;
    }, []);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-card p-12 rounded-lg shadow-elevation-3 border border-border">
          <p className="text-sm font-caption font-medium text-card-foreground mb-4">
            {data?.fullDate}
          </p>
          <p className="text-sm text-muted-foreground">
            Contribution: ${data?.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-sm font-semibold text-primary">
            Total: ${payload?.[0]?.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          {data?.isAutomatic && (
            <div className="flex items-center gap-4 mt-4">
              <Icon name="Zap" size={12} color="var(--color-primary)" />
              <span className="text-xs text-primary">Automatic</span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg p-20 md:p-24 lg:p-28 shadow-elevation-2">
      <div className="flex items-center justify-between mb-20">
        <div className="flex items-center">
          <Icon name="TrendingUp" size={24} color="var(--color-primary)" strokeWidth={2.5} />
          <h3 className="ml-12 text-lg md:text-xl lg:text-2xl font-heading font-semibold text-card-foreground">
            Progress Over Time
          </h3>
        </div>
        {contributions?.length > 0 && (
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Total Contributions</p>
            <p className="text-lg font-bold text-primary">{contributions?.length}</p>
          </div>
        )}
      </div>
      
      {chartData?.length === 0 ? (
        <div className="text-center py-40">
          <Icon name="BarChart3" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-12" />
          <p className="text-muted-foreground">No contributions yet. Add your first contribution to see progress.</p>
        </div>
      ) : (
        <>
          <div className="w-full h-[300px] md:h-[350px] lg:h-[400px]" aria-label="Goal Progress Timeline Chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorProgress" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="date" 
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-caption)' }}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-caption)' }}
                  tickFormatter={(value) => `$${value?.toLocaleString()}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone" 
                  dataKey="cumulativeAmount" 
                  stroke="var(--color-primary)" 
                  strokeWidth={2.5}
                  fill="url(#colorProgress)"
                  dot={{ r: 4, fill: 'var(--color-primary)', strokeWidth: 2, stroke: 'var(--color-card)' }}
                  activeDot={{ r: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          
          {/* Progress Summary */}
          <div className="mt-20 pt-20 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-16">
            <div>
              <p className="text-xs text-muted-foreground mb-4">First Contribution</p>
              <p className="text-sm font-semibold text-foreground">
                {chartData?.[0]?.fullDate}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-4">Latest Contribution</p>
              <p className="text-sm font-semibold text-foreground">
                {chartData?.[chartData?.length - 1]?.fullDate}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-4">Average Contribution</p>
              <p className="text-sm font-semibold text-foreground">
                ${(contributions?.reduce((sum, c) => sum + c?.amount, 0) / contributions?.length)?.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-4">Progress Rate</p>
              <p className="text-sm font-semibold text-success">
                {goal?.targetAmount > 0 ? ((goal?.currentAmount / goal?.targetAmount) * 100)?.toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProgressChart;